import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import axios from 'axios';
import { Bell, ArrowLeft, Mail, Send, Users } from 'lucide-react';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

const NotificationForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAdminAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [clients, setClients] = useState([]);
  const [sendToAll, setSendToAll] = useState(false);
  const [formData, setFormData] = useState({
    user_email: searchParams.get('client') || '',
    title: '',
    message: '',
    type: 'info',
    link: ''
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/admin/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (sendToAll) {
        await axios.post(
          `${API}/notifications/admin/send-bulk`,
          null,
          {
            params: {
              title: formData.title,
              message: formData.message,
              type: formData.type,
              link: formData.link || null
            },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setSuccess('Notificação enviada para todos os clientes!');
      } else {
        await axios.post(
          `${API}/notifications/admin/send`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess('Notificação enviada com sucesso!');
      }
      setTimeout(() => navigate('/admin/dashboard'), 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao enviar notificação');
    } finally {
      setLoading(false);
    }
  };

  const notificationTypes = [
    { value: 'info', label: 'Informação', color: 'bg-blue-100 text-blue-700' },
    { value: 'success', label: 'Sucesso', color: 'bg-green-100 text-green-700' },
    { value: 'warning', label: 'Aviso', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'contract', label: 'Contrato', color: 'bg-purple-100 text-purple-700' },
    { value: 'document', label: 'Documento', color: 'bg-indigo-100 text-indigo-700' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-md transition-colors"
        >
          <ArrowLeft size={24} className="text-gray-600" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Bell className="text-arcano-blue" />
            Nova Notificação
          </h1>
          <p className="text-gray-600">Envie uma notificação para clientes</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-8 max-w-2xl">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md mb-6">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Toggle: Single or All */}
          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-md">
            <button
              type="button"
              onClick={() => setSendToAll(false)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-md transition-colors ${
                !sendToAll ? 'bg-arcano-blue text-white' : 'bg-white text-gray-700 border'
              }`}
            >
              <Mail size={20} />
              Cliente Específico
            </button>
            <button
              type="button"
              onClick={() => setSendToAll(true)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-md transition-colors ${
                sendToAll ? 'bg-arcano-blue text-white' : 'bg-white text-gray-700 border'
              }`}
            >
              <Users size={20} />
              Todos os Clientes
            </button>
          </div>

          {!sendToAll && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Cliente *</label>
              <select
                value={formData.user_email}
                onChange={(e) => setFormData({ ...formData, user_email: e.target.value })}
                required={!sendToAll}
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
              >
                <option value="">Selecione um cliente</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.email}>
                    {client.full_name} ({client.email})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Notificação</label>
            <div className="grid grid-cols-5 gap-2">
              {notificationTypes.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, type: type.value })}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-all ${
                    formData.type === type.value
                      ? `${type.color} ring-2 ring-offset-2 ring-arcano-blue`
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Título *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              maxLength={100}
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
              placeholder="Título da notificação"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Mensagem *</label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              required
              rows={4}
              maxLength={500}
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent resize-none"
              placeholder="Escreva a mensagem da notificação..."
            />
            <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 caracteres</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Link (opcional)</label>
            <input
              type="url"
              value={formData.link}
              onChange={(e) => setFormData({ ...formData, link: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
              placeholder="https://..."
            />
            <p className="text-xs text-gray-500 mt-1">Link que o cliente pode acessar ao clicar na notificação</p>
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 bg-arcano-blue text-white px-4 py-3 rounded-md hover:bg-arcano-blue-dark transition-colors disabled:opacity-50"
            >
              <Send size={20} />
              {loading ? 'Enviando...' : 'Enviar Notificação'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NotificationForm;
